import apiClient from "services/axios";
import config from "config/config";
import store from "store";

const API = {
  detail: "/api/v1/settings/default",
  update: "/api/v1/settings/default",
  getDatetime: "/api/v1/settings/datetime",
  setDatetime: "/api/v1/settings/datetime",
  shutdown: "/api/v1/settings/shutdown",
  factoryReset: "/api/v1/settings/factory-reset",
  factoryResetStatus: "/api/v1/settings/factory-reset-status",
  exportConfig: "/api/v1/settings/export",
  importConfig: "/api/v1/settings/import",
  maintenance: "/api/v1/settings/maintenance",
};

export const detail = async () => {
  const response = await apiClient.get(`${API.detail}`);
  if (response) {
    return response.data;
  }
  return false;
};

export const update = async (body) => {
  let payload = body;
  if (body.logoFile) {
    payload = new FormData();
    payload.append("brandName", body.brandName);
    payload.append("logoFile", body.logoFile);
    payload.append("title", body.title);
    payload.append("description", body.description || "");
  }
  const response = await apiClient.patch(`${API.update}`, payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  if (response) {
    return response.data;
  }
  return false;
};

export const getDatetime = async () => {
  const response = await apiClient.get(`${API.getDatetime}`);
  if (response) {
    return response.data;
  }
  return false;
};

export const setDatetime = async (body) => {
  const response = await apiClient.put(API.setDatetime, body);
  if (response) {
    return response.data;
  }
  return false;
};

export const shutdown = async () => {
  const response = await apiClient.post(API.shutdown);
  if (response) {
    return response.data;
  }
  return false;
};

export const factoryReset = async (body) => {
  const response = await apiClient.post(API.factoryReset, body);
  if (response) {
    return response.data;
  }
  return false;
};

export const factoryResetStatus = async () => {
  const response = await apiClient.get(API.factoryResetStatus);
  if (response) {
    return response.data;
  }
  return false;
};

export const exportConfig = async () => {
  const response = await apiClient.get(`${API.exportConfig}`, { responseType: "blob" });
  if (response) {
    const href = URL.createObjectURL(response.data);

    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", "uSense.config");
    link.click();

    URL.revokeObjectURL(href);
    return true;
  }
  return false;
};

export const importConfig = async (body) => {
  let payload = body;
  if (body.configFile) {
    payload = new FormData();
    payload.append("configFile", body.configFile);
  }

  const response = await apiClient.post(API.importConfig, payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  if (response) {
    return response.data;
  }
  return false;
};

export const getMaintenanceSummary = async () => {
  const response = await apiClient.get(`${API.maintenance}/summary`, { propagateError: true });
  return response.data;
};

export const createMaintenanceExport = async (body) => {
  const response = await apiClient.post(`${API.maintenance}/exports`, body, {
    timeout: 0,
    propagateError: true,
  });
  return response.data;
};

export const getMaintenanceJob = async (id) => {
  const response = await apiClient.get(`${API.maintenance}/jobs/${id}`, { propagateError: true });
  return response.data;
};

export const heartbeatMaintenanceJob = async (id) => {
  const response = await apiClient.post(`${API.maintenance}/jobs/${id}/heartbeat`, null, { propagateError: true });
  return response.data;
};

export const downloadMaintenanceExport = async (id, filename, signal) => {
  const downloadName = filename || "uSense-backup.usense";
  if (typeof window.showSaveFilePicker === "function") {
    const handle = await window.showSaveFilePicker({
      suggestedName: downloadName,
      types: [{ description: "uSense Backup", accept: { "application/octet-stream": [".usense"] } }],
    });
    const token = store.get("token");
    const endpoint = `${config.API_ENDPOINT.replace(/\/$/, "")}${API.maintenance}/exports/${id}/download`;
    const response = await fetch(endpoint, {
      headers: token?.token ? { authorization: token.token } : {},
      signal,
    });
    if (!response.ok) throw new Error(`Backup download failed (${response.status})`);
    const writable = await handle.createWritable();
    try {
      if (!response.body) throw new Error("Streaming download is not supported by this browser.");
      await response.body.pipeTo(writable);
    } catch (error) {
      await writable.abort();
      throw error;
    }
    return { nativeDownload: false };
  }
  const tokenResponse = await apiClient.post(`${API.maintenance}/exports/${id}/download-token`, null, {
    propagateError: true,
  });
  const endpoint = `${config.API_ENDPOINT.replace(/\/$/, "")}${API.maintenance}/download/${tokenResponse.data.token}`;
  const link = document.createElement("a");
  link.href = endpoint;
  link.setAttribute("download", downloadName);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  return { nativeDownload: true };
};

export const uploadMaintenanceImport = async (file, signal, onProgress) => {
  const filename = encodeURIComponent(file.name);
  const response = await apiClient.post(`${API.maintenance}/imports?filename=${filename}`, file, {
    headers: {
      "Content-Type": "application/octet-stream",
      "X-Backup-Filename": encodeURIComponent(file.name),
    },
    timeout: 0,
    signal,
    onUploadProgress: (event) => {
      if (!onProgress) return;
      const total = event.total || file.size || 0;
      onProgress({ loaded: event.loaded || 0, total });
    },
    propagateError: true,
  });
  return response.data;
};

export const previewMaintenanceImport = async ({ id, password, frontendVersion, availableSystemComponents, signal }) => {
  const response = await apiClient.post(`${API.maintenance}/imports/${id}/preview`, {
    password: password || "",
    frontendVersion: frontendVersion || "",
    availableSystemComponents: availableSystemComponents || [],
  }, {
    timeout: 0,
    signal,
    propagateError: true,
  });
  return response.data;
};

export const applyMaintenanceImport = async (id, body) => {
  const response = await apiClient.post(`${API.maintenance}/imports/${id}/apply`, body, {
    timeout: 0,
    propagateError: true,
  });
  return response.data;
};

export const getMaintenanceImportStatus = async (token) => {
  const response = await apiClient.get(`${API.maintenance}/import-status/${encodeURIComponent(token)}`, {
    propagateError: true,
  });
  return response.data;
};

export const deleteMaintenanceJob = async (id) => {
  await apiClient.delete(`${API.maintenance}/jobs/${id}`, { propagateError: true });
};

export const getSoftwareUpgradeStatus = async () => {
  const response = await apiClient.get(`${API.maintenance}/software-upgrade/status`, { propagateError: true });
  return response.data;
};

export const uploadSoftwareRelease = async (file, onProgress) => {
  const filename = encodeURIComponent(file.name);
  const response = await apiClient.post(`${API.maintenance}/software-upgrades?filename=${filename}`, file, {
    headers: { "Content-Type": "application/octet-stream", "X-Release-Filename": filename },
    timeout: 0,
    onUploadProgress: (event) => {
      if (!onProgress) return;
      onProgress({ loaded: event.loaded || 0, total: event.total || file.size || 0 });
    },
    propagateError: true,
  });
  return response.data;
};

export const inspectSoftwareRelease = async (id) => {
  const response = await apiClient.post(`${API.maintenance}/software-upgrades/${id}/inspect`, null, {
    timeout: 0, propagateError: true,
  });
  return response.data;
};

export const applySoftwareRelease = async (id) => {
  const response = await apiClient.post(`${API.maintenance}/software-upgrades/${id}/apply`, null, {
    timeout: 0, propagateError: true,
  });
  return response.data;
};

export const getSoftwareUpgradeJob = async (id, publicStatus = false) => {
  const path = publicStatus
    ? `${API.maintenance}/software-upgrade-status/${encodeURIComponent(id)}`
    : `${API.maintenance}/software-upgrades/${encodeURIComponent(id)}`;
  const response = await apiClient.get(path, { propagateError: true });
  return response.data;
};

export const deleteSoftwareUpgradeJob = async (id) => {
  await apiClient.delete(`${API.maintenance}/software-upgrades/${encodeURIComponent(id)}`, { propagateError: true });
};
